import { Helmet } from 'react-helmet-async';

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  noindex?: boolean;
  breadcrumbs?: BreadcrumbItem[];
  jsonLd?: Record<string, unknown>;
  ogType?: string;
}

const SEOHead = ({ 
  title = "Valmira - Premium Agency Ad Accounts | Scale Your Campaigns",
  description = "Get instant access to premium agency ad accounts for Meta, TikTok, Snapchat, Pinterest and 40+ platforms. Unlimited spend, compliance guaranteed, 24/7 support.",
  keywords = "agency ad accounts, Meta ads, TikTok ads, Snapchat ads, Pinterest ads, digital marketing, advertising agency, ad account access, unlimited ad spend",
  canonicalUrl,
  ogImage = "https://valmira-agency.com/valmira-logo.png",
  noindex = false,
  breadcrumbs,
  jsonLd,
  ogType = "website",
}: SEOHeadProps) => {
  const finalCanonicalUrl = canonicalUrl || `https://valmira-agency.com${window.location.pathname}`;

  const breadcrumbJsonLd = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url,
    })),
  } : null;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <link rel="canonical" href={finalCanonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={finalCanonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Valmira" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@valmira" />
      <meta name="twitter:creator" content="@valmira" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Breadcrumb JSON-LD */}
      {breadcrumbJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
      )}

      {/* Custom JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
